// Admin → Integrations → Google Workspace. Single-slot (one workspace
// domain per org). UI lets the admin update domain + admin email,
// paste a new service account JSON (overwrites in vault), test the
// connection by listing delegated users, and trigger a calendar sync.

import {
  loadIntegrationCatalogue,
  saveIntegration,
  deleteIntegration
} from '../../integrations/index.js'

const KIND = 'google_workspace'
const SLUG = 'default'

const _loadInstance = async (root) => {
  const orgId = root?.jwtClaims?.workspace_org_id
  if (!orgId) {
    if (!root._googleHubWatcher) {
      root._googleHubWatcher = setInterval(() => {
        if (root?.jwtClaims?.workspace_org_id) {
          clearInterval(root._googleHubWatcher)
          root._googleHubWatcher = null
          _loadInstance(root)
        }
      }, 300)
    }
    return
  }
  const data = await loadIntegrationCatalogue(orgId)
  root.update({ _integrationHub: data })
}

const _triggerCalendarSync = async (root) => {
  const orgId = root?.jwtClaims?.workspace_org_id
  if (!orgId) return { ok: false, error: 'no active workspace' }
  // TODO[sdk-edge-invoke]: calendar-sync is a governance-tier Supabase edge
  // function (not a workspace wrapper route). The SDK has no flat method
  // for invoking arbitrary edge functions on the governance project; once
  // a /workspace/integrations/sync admin route or a callIntegrationOp
  // 'sync' op ships, replace with
  // `getSDK().execute('workspaceProject.integrations', 'sync', {...})`.
  return {
    ok: false,
    error: 'calendar-sync edge function invocation not yet routed through SDK; ' +
      'pending wrapper /integrations/sync endpoint'
  }
}

const _currentInstance = (root) => {
  const list = root?._integrationHub?.integrations || []
  return list.find((i) => i.kind === KIND && i.slug === SLUG)
}

export const adminIntegrationsGoogle = {
  extends: 'AppPage',
  metadata: { title: 'Google Workspace / Integrations / Symbols Workspace' },
  onInit: (el, s) => {
    if (!el.call('shouldRedirectNonAdmin', s)) return
    const root = el.call('findRouteRoot', el)
    if (root) el.router('/', root, {}, { pushState: true })
  },
  onCreate: (el, s) => _loadInstance(s.root),

  AdminSidebar: {},

  AdminMain: {
    extends: 'AppMain',
    padding: '0',

    Header: {
      extends: 'PageHeader',
      LeftGroup: {
        BackLink: {
          tag: 'button', type: 'button',
          padding: '0', background: 'transparent', border: 'none',
          color: 'caption', fontSize: 'Z2', cursor: 'pointer',
          text: '← Integrations',
          onClick: (e, el, s) => {
            const root = el.call('findRouteRoot', el)
            if (root) el.router('/admin/integrations', root)
          }
        },
        Title: { text: 'Google Workspace' }
      }
    },

    Body: {
      overflow: 'auto',
      padding: 'B',
      flow: 'y',
      gap: 'A',

      Hint: {
        tag: 'p', fontSize: 'Z2', color: 'caption', margin: '0',
        text: 'Domain-wide delegation authorizes governance to sync calendars on behalf of every user in the workspace. Rotate the service account JSON to revoke access.'
      },

      Card: { extends: 'GoogleConfigCard' }
    }
  }
}

export const GoogleConfigCard = {
  round: 'Z1',
  background: 'overlay',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'line',
  padding: 'A',
  flow: 'y',
  gap: 'Z1',

  StatusRow: {
    flow: 'x', align: 'center space-between', gap: 'Z',
    Left: {
      flow: 'x', align: 'center', gap: 'Z1',
      Icon: { icon: 'google', boxSize: 'A' },
      Info: {
        flow: 'y', gap: 'V',
        Name: {
          tag: 'span', fontSize: 'A', fontWeight: '700', color: 'title',
          text: (el, s) => {
            const inst = _currentInstance(s.root)
            return inst?.config?.domain || 'Not configured'
          }
        },
        Status: {
          tag: 'span', fontSize: 'Y2', color: 'caption',
          text: (el, s) => {
            const inst = _currentInstance(s.root)
            if (!inst) return 'Not configured'
            if (!inst.has_secret) return 'Config only (no service account key)'
            return inst.enabled ? 'Configured · enabled' : 'Configured · disabled'
          }
        }
      }
    },

    Right: {
      flow: 'x', gap: 'Z',

      SyncBtn: {
        tag: 'button', type: 'button',
        hide: (el, s) => !(_currentInstance(s.root)?.has_secret),
        padding: 'V Z', round: 'Y',
        background: 'blue', color: 'white',
        fontSize: 'Y2', fontWeight: '700',
        cursor: 'pointer', border: 'none',
        text: (el, s) => (s.root?._googleSyncing ? 'Syncing…' : 'Pull calendar'),
        onClick: async (e, el, s) => {
          s.root.update({ _googleSyncing: true })
          const { ok, error } = await _triggerCalendarSync(s.root)
          s.root.update({ _googleSyncing: false })
          if (ok) el.call('openNotification', { title: 'Sync started', type: 'success' })
          else el.call('openNotification', { title: 'Sync failed', message: error, type: 'error' })
        }
      }
    }
  },

  Fields: {
    flow: 'y',
    gap: 'Z1',
    childExtends: 'GoogleField',
    childrenAs: 'state',
    children: () => [
      { label: 'Workspace domain', placeholder: 'symbols.app', configKey: 'domain' },
      { label: 'Admin email (DWD subject)', placeholder: 'nika@symbols.app', configKey: 'admin_email' }
    ]
  },

  Secret: {
    flow: 'y', gap: 'V',
    Label: {
      tag: 'label', fontSize: 'Y2', color: 'caption', fontWeight: '700',
      text: 'Service account JSON (paste new to rotate — leave empty to keep)'
    },
    Textarea: {
      tag: 'textarea',
      rows: '6',
      width: '100%',
      padding: 'Z', round: 'Y',
      background: 'document',
      borderWidth: '1px', borderStyle: 'solid', borderColor: 'line',
      color: 'title', fontFamily: 'mono', fontSize: 'Z2',
      placeholder: '{ "type": "service_account", ... }',
      onInput: (e, el, s) => {
        s.root.update({
          _googleDraft: {
            ...(s.root?._googleDraft || {}),
            secret: e.target.value
          }
        })
      }
    },
    Hint: {
      tag: 'p', fontSize: 'Y2', color: 'caption', margin: '0',
      text: 'Must have domain-wide delegation and the calendar read/write scopes authorized in Google Admin.'
    }
  },

  Actions: {
    flow: 'x', gap: 'Z', align: 'center space-between', marginTop: 'Z',

    RemoveBtn: {
      tag: 'button', type: 'button',
      hide: (el, s) => !_currentInstance(s.root),
      padding: 'V Z', round: 'Y',
      background: 'transparent',
      borderWidth: '1px', borderStyle: 'solid', borderColor: 'line',
      color: 'red', fontSize: 'Y2', cursor: 'pointer',
      text: 'Remove integration',
      onClick: async (e, el, s) => {
        if (!window.confirm('Remove Google Workspace integration? Calendar sync will stop.')) return
        const orgId = s.root?.jwtClaims?.workspace_org_id
        const { ok } = await deleteIntegration({ orgId, kind: KIND, slug: SLUG })
        if (ok) {
          await _loadInstance(s.root)
          el.call('openNotification', { title: 'Removed', type: 'success' })
        }
      }
    },

    SaveBtn: {
      tag: 'button', type: 'button',
      padding: 'V A', round: 'Y',
      background: 'blue', color: 'white',
      fontSize: 'Y2', fontWeight: '700',
      cursor: 'pointer', border: 'none',
      text: 'Save',
      onClick: async (e, el, s) => {
        const orgId = s.root?.jwtClaims?.workspace_org_id
        if (!orgId) return
        const inst = _currentInstance(s.root)
        const draft = s.root?._googleDraft || {}
        const config = {
          ...(inst?.config || {}),
          ...(draft.config || {})
        }
        if (!config.domain) {
          el.call('openNotification', { title: 'Domain required', type: 'error' })
          return
        }
        const { ok, body } = await saveIntegration({
          orgId, kind: KIND, slug: SLUG,
          displayName: config.domain,
          secret: draft.secret || undefined,
          config,
          enabled: true
        })
        if (ok) {
          s.root.update({ _googleDraft: null })
          await _loadInstance(s.root)
          el.call('openNotification', { title: 'Saved', type: 'success' })
        } else {
          el.call('openNotification', {
            title: 'Save failed',
            message: body?.error || 'Server error',
            type: 'error'
          })
        }
      }
    }
  }
}

export const GoogleField = {
  flow: 'y', gap: 'V',
  Label: {
    tag: 'label', fontSize: 'Y2', color: 'caption', fontWeight: '700',
    text: (el, s) => s.label
  },
  Input: {
    tag: 'input', type: 'text',
    width: '100%', padding: 'Z', round: 'Y',
    background: 'document',
    borderWidth: '1px', borderStyle: 'solid', borderColor: 'line',
    color: 'title', fontSize: 'Z2',
    placeholder: (el, s) => s.placeholder || '',
    value: (el, s) => {
      const draft = s.root?._googleDraft?.config?.[s.configKey]
      if (draft != null) return draft
      const inst = _currentInstance(s.root)
      return inst?.config?.[s.configKey] || ''
    },
    onInput: (e, el, s) => {
      s.root.update({
        _googleDraft: {
          ...(s.root?._googleDraft || {}),
          config: {
            ...(((s.root?._googleDraft || {}).config) || {}),
            [s.configKey]: e.target.value
          }
        }
      })
    }
  }
}
